import React, { useCallback, useEffect, useReducer, useState } from 'react';
import {
    AuthClient,
    AuthClientOptions,
    CacheLocation,
    LogoutOptions,
    LogoutUrlOptions,
    RedirectLoginOptions as AuthJSRedirectLoginOptions,
    AuthLoginOptions as AuthJSLoginOptions,
    AuthenticationResult,
    AuthSignupOptions as AuthJSSignupOptions,
    AuthConfirmSignupOptions as AuthJSConfirmSignupOptions,
    AuthSignupResult,
    AuthConfirmSignupResult,
    AuthConfirmSignupOptions,
    AuthPasswordResetOptions as AuthJSPasswordResetOptions,
    AuthConfirmPasswordResetOptions as AuthJSConfirmPasswordResetOptions,
    AuthPasswordResetResult,
    AuthConfirmPasswordResetResult,
    // GetTokenSilentlyOptions,
} from './js';
import {VERSION} from './js/constants'
import { AuthContext } from './authContext';
import { hasAuthParams } from './utils';
import {loginError, signupError, confirmSignupError, tokenError} from './errors';
import { reducer } from './reducer';
import { initialAuthState } from "../_appData";
import {
    AppState,
    AuthProviderOptions,
    AuthCodeLoginOptions,
    AuthPasswordLoginOptions,
    AccessTokenGetOptions,
    AuthSignupOptions,
    AuthPasswordResetOptions, AuthConfirmPasswordResetOptions
} from "./types";
import {IApiResponse, IAuthTokenResponse, IAuthUriResponse, IPafApplication} from "../_api";


/**
 * @ignore
 */
const toJSAuthClientOptions = (
  opts: AuthProviderOptions
): AuthClientOptions => {
  const { clientId, redirectUri, maxAge, ...validOpts } = opts;
  return {
    ...validOpts,
    client_id: clientId,
    redirect_uri: redirectUri,
    max_age: maxAge,
    authClient: {
      name: 'auth0-react',
      version: VERSION,
    },
  };
};

/**
 * @ignore
 */
const toJSAuthLoginRedirectOptions = (
  opts?: AuthCodeLoginOptions
): AuthJSRedirectLoginOptions | undefined => {
  if (!opts) {
    return;
  }
  const { redirectUri, ...validOpts } = opts;

  return {
    ...validOpts,
    redirect_uri: redirectUri,
  };
};

/**
 * @ignore
 */
const toJSAuthLoginPasswordOptions = (
  opts?: AuthPasswordLoginOptions
): AuthJSLoginOptions | undefined => {
  if (!opts) {
    return;
  }
  const { ...validOpts } = opts;

  return {
    ...validOpts,
  };
};

/**
 * @ignore
 */
const toJSAuthSignupOptions = (
  opts?: AuthSignupOptions
): AuthJSSignupOptions | undefined => {
  if (!opts) {
    return;
  }
  const {
      first_name,
      last_name,
      email,
      phone_number,
      password,
      groups
  } = opts;

  return {
      first_name: first_name,
      last_name: last_name,
      email:email,
      phone_number: phone_number,
      password: password,
      groups: groups,
  };
};

/**
 * @ignore
 */
const toJSAuthConfirmSignupOptions = (
  opts?: AuthConfirmSignupOptions
): AuthJSConfirmSignupOptions | undefined => {
  if (!opts) {
    return;
  }
  const {
      sub,
      first_name,
      last_name,
      email,
      phone_number,
      code,
      group
      // officer_title,
      // fax,
      // federal_tax_id
  } = opts;

  return {
      sub: sub,
      first_name: first_name,
      last_name: last_name,
      email:email,
      phone_number: phone_number,
      code: code,
      group: group
      // officer_title: officer_title,
      // fax: fax,
      // federal_tax_id: federal_tax_id,
  };
};


/**
 * @ignore
 */
const toJSAuthPasswordResetOptions = (
  opts?: AuthPasswordResetOptions
): AuthJSSignupOptions | undefined => {
  if (!opts) {
    return;
  }
  const {
      email,
  } = opts;

  return {
      email:email,
  };
};

/**
 * @ignore
 */
const toJSAuthConfirmPasswordResetOptions = (
  opts?: AuthConfirmPasswordResetOptions
): AuthJSConfirmPasswordResetOptions | undefined => {
  if (!opts) {
    return;
  }
  const {
      email,
      password,
      code
  } = opts;

  return {
      email:email,
      password: password,
      code: code,
  };
};

/**
 * @ignore
 */
const defaultOnRedirectCallback = (appState?: AppState): void => {
  window.history.replaceState(
    {},
    document.title,
    appState?.returnTo || window.location.pathname
  );
};

const myDefaultOnRedirectCallback = (appState?: AppState): void => {
  window.history.replaceState(
    {},
    document.title,
    appState?.returnTo || window.location.pathname
  );
};

/**
 * ```jsx
 * <AuthProvider
 *   domain={domain}
 *   clientId={clientId}
 *   redirectUri={window.location.origin}>
 *   <App />
 * </AuthProvider>
 * ```
 *
 * Provides the AuthContext to its child components.
 */
export const AuthProvider = (opts: AuthProviderOptions): JSX.Element => {
  const {
    children,
    skipRedirectCallback,
    onRedirectCallback = defaultOnRedirectCallback,
    ...clientOpts
  } = opts;
  const [client] = useState(
    () => new AuthClient(toJSAuthClientOptions(clientOpts))
  );
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  useEffect(() => {
    (async (): Promise<void> => {
        try {
            // debugger;
            if (hasAuthParams()) {
                const { appState } = await client.handleRedirectCallback();
                onRedirectCallback(appState);
            } else {
                // Try to get user from cache first
                let user = await client.getUser();
                
                // If no user found in cache, try to refresh the token
                if (!user) {
                    try {
                        await client.getAccessToken();
                        // After token refresh, try to get user again
                        user = await client.getUser();
                    } catch (error) {
                        // Token refresh failed, user needs to login again
                       return
                    }
                }
                
                dispatch({ type: 'INITIALISED', user });
                // Sync navigation state from cookie (e.g. after refresh on /company/register)
                const navState = await client.canNavigate();
                dispatch({ type: 'UPDATE_NAVIGATION_STATE', canNavigateState: navState });
                return;
            }
            const user = await client.getUser();
            dispatch({ type: 'INITIALISED', user });
            const navState = await client.canNavigate();
            dispatch({ type: 'UPDATE_NAVIGATION_STATE', canNavigateState: navState });
          } catch (error) {
            dispatch({ type: 'ERROR', error: loginError(error) });
          }
      })();
  }, [client, onRedirectCallback]);

  const getAuthorizeUrl = useCallback(
    (opts?: AuthCodeLoginOptions): Promise<IAuthUriResponse> =>
      client.getAuthorizeUrl(toJSAuthLoginRedirectOptions(opts) || {} as AuthJSRedirectLoginOptions),
    [client]
  );

  const getLogoutUrl = useCallback(
    (opts?: LogoutUrlOptions): string => client.buildLogoutUrl(opts),
    [client]
  );

  const loginWithPassword = useCallback(
    async (opts?: AuthPasswordLoginOptions): Promise<AuthenticationResult> => {
        const resp = await client.loginWithPassword(toJSAuthLoginPasswordOptions(opts));
        if (resp && resp.state === "success") {
            const user = resp.user;
            dispatch({ type: 'LOGIN', user });
        }
        return resp;
    },
    [client]
  );

  const loginWithRedirect = useCallback(
    (opts?: AuthCodeLoginOptions): Promise<void> =>
        client.loginWithRedirect(toJSAuthLoginRedirectOptions(opts)),
    [client]
  );

  const logout = useCallback(
    (opts: LogoutOptions = {}): void => {
      client.logout(opts);
      if (opts.localOnly) {
        dispatch({ type: 'LOGOUT' });
      }
    },
    [client]
  );

  const signup = useCallback(
    async (opts?: AuthSignupOptions): Promise<AuthSignupResult> => {
        let resp: AuthSignupResult = {
            state: "error",
            signupResult: {
                user_confirmed: false,
                user_sub: "",
                code_delivery_details: {
                    destination: "",
                    delivery_medium: "EMAIL",
                    attribute_name: "",
                }
            },
        };

        try{
            const signupResult = await client.signup(toJSAuthSignupOptions(opts));
            if (signupResult && signupResult.state === "success") {
                resp = signupResult;
            }
        }
        catch (e) {
            throw confirmSignupError(e);
        }

        return resp;
    },
        [client]
  );

  const signupConfirm = useCallback(
    async (opts?: AuthConfirmSignupOptions): Promise<AuthConfirmSignupResult> => {
        let resp: AuthConfirmSignupResult = {
            state: "error",
        };

        try{
            const signupResult = await client.signupConfirm(toJSAuthConfirmSignupOptions(opts));
            if (signupResult) {
                resp.state = signupResult.state;
            }
        }
        catch (e) {
            throw confirmSignupError(e);
        }

        return resp;
      },
      [client]
  );

  const passwordReset = useCallback(
    async (opts?: AuthPasswordResetOptions): Promise<AuthPasswordResetResult> => {
        let resp: AuthPasswordResetResult = {
            state: "error",
            passwordResetResult: {
                code_delivery_details: {
                    destination: "",
                    delivery_medium: "EMAIL",
                    attribute_name: "",
                }
            },
        };

        try{
            const pwdResetResult = await client.passwordReset(toJSAuthPasswordResetOptions(opts));
            if (pwdResetResult && pwdResetResult.state === "success") {
                resp = pwdResetResult;
            }
        }
        catch (e) {
            throw confirmSignupError(e);
        }

        return resp;
    },
        [client]
  );

  const passwordResetConfirm = useCallback(
    async (opts?: AuthConfirmPasswordResetOptions): Promise<AuthConfirmPasswordResetResult> => {
        let resp: AuthConfirmPasswordResetResult = {
            state: "error",
        };

        try{
            const pwdResetResult = await client.passwordResetConfirm(toJSAuthConfirmPasswordResetOptions(opts));
            if (pwdResetResult) {
                resp.state = pwdResetResult.state;
            }
        }
        catch (e) {
            throw confirmSignupError(e);
        }

        return resp;
      },
      [client]
  );


  const userUpdatedAt = state.user?.updated_at;

  const getAccessToken = useCallback(
    async (opts?: AccessTokenGetOptions): Promise<string> => {
      let token;
      try {
        token = await client.getAccessToken(opts);
      } catch (error) {
        throw tokenError(error);
      }
      const user = await client.getUser();
      if (user?.updated_at !== userUpdatedAt) {
        dispatch({ type: 'USER_UPDATED', user });
      }
      return token;
    },
    [client, userUpdatedAt]
  );

  const userPrivileges = state?.user?.privileges;

  const userHasPrivilege = useCallback((authPrivileges: Array<string>): boolean => {
      // check user has privilege
      let hasPrivilege = false;
      if (authPrivileges && authPrivileges.length > 0){
          const auth_privileges = authPrivileges.map((priv: string) => priv.toLowerCase());
          if (userPrivileges) {
              for (let user_priv of userPrivileges) {
                  if (auth_privileges.indexOf(user_priv.toLowerCase()) > -1) {
                      hasPrivilege = true;
                      break;
                  }
              }
          }
      }
      else {
          hasPrivilege = true;
      }

      return hasPrivilege;
  },
    [userPrivileges]
  );

  const toggleNavigation = async (): Promise<void> => {
      await client.toggleNavigation();
      // Update navigation state after toggling
      const navState = await client.canNavigate();
      dispatch({ type: 'UPDATE_NAVIGATION_STATE', canNavigateState: navState });
    }
    

  const canNavigate = useCallback(
    async (): Promise<boolean> => {
      const navState = await client.canNavigate();
      dispatch({ type: 'UPDATE_NAVIGATION_STATE', canNavigateState: navState });
      return navState;
    },
    [client, dispatch]
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        getAuthorizeUrl,
        getLogoutUrl,
        getAccessToken,
        userHasPrivilege,
        loginWithPassword,
        loginWithRedirect,
        logout,
        signup,
        signupConfirm,
        passwordReset,
        passwordResetConfirm,
        toggleNavigation,
        canNavigate,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
